import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Copy, Gift, Info, Loader2, Mail, Share2 } from 'lucide-react';
import { SiWhatsapp, SiGmail, SiFacebook, SiLinkedin, SiX } from 'react-icons/si';
import { Button } from '../../../components/ui/button';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '../../../components/ui/tooltip';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import useReferralShare from '../../../hooks/useReferralShare';
import getMyReferrals from '../../../api/referrals/getMyReferrals';
import { MyReferralsResponseI } from '../../../api/referrals/interfaces/Referrals';
import { analytics, ANALYTICS_EVENTS } from '../../../services/analytics';
import { UserPlanStatus } from '../../../api/users/interfaces/Users';
import { buildReferralMessage } from '../../../utils/referralShare';
import AttributionsTable from './AttributionsTable';

interface MyReferralsViewProps {
	planStatus?: UserPlanStatus;
}

const MyReferralsView: React.FC<MyReferralsViewProps> = ({ planStatus }) => {
	const { token } = useContext(ArcaContext);
	const isMobile = useIsMobile();

	const [data, setData] = useState<MyReferralsResponseI | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [messageText, setMessageText] = useState('');
	const [linkCopied, setLinkCopied] = useState(false);
	const [messageCopied, setMessageCopied] = useState(false);

	const {
		shareNative,
		shareWhatsApp,
		shareGmail,
		shareEmail,
		shareTwitter,
		shareLinkedIn,
		shareFacebook
	} = useReferralShare(data?.url ?? '', messageText);

	useEffect(() => {
		analytics.track(ANALYTICS_EVENTS.REFERRALS_PAGE_VIEWED);
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
		// token comes from context — including it would re-trigger on every token refresh
	}, []);

	useEffect(() => {
		if (!linkCopied) return;
		const timer = setTimeout(() => setLinkCopied(false), 2000);
		return () => clearTimeout(timer);
	}, [linkCopied]);

	useEffect(() => {
		if (!messageCopied) return;
		const timer = setTimeout(() => setMessageCopied(false), 2000);
		return () => clearTimeout(timer);
	}, [messageCopied]);

	const loadData = async () => {
		if (!token) return;
		setLoading(true);
		setError(false);
		try {
			const response = await getMyReferrals(token);
			setData(response);
			setMessageText(buildReferralMessage(response.url));
		} catch {
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const copyLink = async () => {
		if (!data) return;
		await navigator.clipboard.writeText(data.url);
		analytics.track(ANALYTICS_EVENTS.REFERRAL_LINK_COPIED);
		setLinkCopied(true);
	};

	const copyMessage = async () => {
		await navigator.clipboard.writeText(messageText);
		analytics.track(ANALYTICS_EVENTS.REFERRAL_MESSAGE_COPIED);
		setMessageCopied(true);
	};

	if (loading) {
		return (
			<div className="individual-v2 -mx-4 -mt-4 min-h-screen flex items-center justify-center">
				<Loader2 className="w-10 h-10 animate-spin text-primary" />
			</div>
		);
	}

	if (!data) {
		if (error) {
			return (
				<div className="individual-v2 -mx-4 -mt-4 min-h-screen flex items-center justify-center">
					<div className="text-center space-y-3">
						<p className="text-sm text-muted-foreground">No hemos podido cargar tus datos.</p>
						<Button variant="outline" size="sm" onClick={loadData}>
							Reintentar
						</Button>
					</div>
				</div>
			);
		}
		return null;
	}

	const hasCompletedTransfer = planStatus === UserPlanStatus.ACTIVE_IN_ARCA;

	return (
		<TooltipProvider>
			<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
				<div className="max-w-4xl mx-auto space-y-8 p-4 pt-12 lg:p-6 lg:pt-14">
					{/* Header */}
					<motion.div
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4 }}
					>
						<div className="flex flex-col gap-2">
							<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
								Invita y gana
							</h1>
							<p className="text-base text-muted-foreground">
								Consigue{' '}
								<span className="font-medium text-foreground">
									{data.reward_snapshot.display_name}
								</span>{' '}
								por cada amigo que traiga su plan de pensiones antiguo a Arca Digital. En cuanto
								complete el traspaso, sumaremos una nueva aportación directamente a tu jubilación.
							</p>
						</div>
					</motion.div>

					{/* Card — compartir */}
					<motion.div
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.4, delay: 0.1 }}
					>
						<div className="surface-card p-5 lg:p-6 space-y-5">
							{/* Only shown when the referrer hasn't completed their own transfer yet (planStatus !== ACTIVE_IN_ARCA) */}
							{!hasCompletedTransfer && (
								<div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 text-sm text-amber-800">
									<Info className="w-4 h-4 shrink-0" />
									Completa tu propio traspaso para poder redimir los premios
								</div>
							)}

							{/* Link */}
							<div>
								<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-2">
									Tu link de invitación
								</p>
								<div className="flex gap-2">
									<input
										readOnly
										value={data.url}
										onClick={(e) => (e.target as HTMLInputElement).select()}
										className="input-arca flex-1 text-sm cursor-pointer py-2"
									/>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="outline"
												size="sm"
												onClick={copyLink}
												aria-label="Copiar link"
												className="shrink-0 transition-colors"
											>
												{linkCopied ? (
													<Check className="w-4 h-4 text-primary" />
												) : (
													<Copy className="w-4 h-4" />
												)}
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											<p className="text-xs">{linkCopied ? 'Link copiado' : 'Copiar link'}</p>
										</TooltipContent>
									</Tooltip>
								</div>
							</div>

							{/* Mensaje */}
							<div className="space-y-1.5">
								<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
									Mensaje sugerido (edítalo si quieres)
								</p>
								<textarea
									value={messageText}
									onChange={(e) => setMessageText(e.target.value)}
									rows={4}
									className="input-arca w-full text-sm resize-none"
								/>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											variant="outline"
											size="sm"
											onClick={copyMessage}
											aria-label="Copiar mensaje"
											className="w-full transition-colors"
										>
											{messageCopied ? (
												<Check className="w-4 h-4 mr-2 text-primary" />
											) : (
												<Copy className="w-4 h-4 mr-2" />
											)}
											{messageCopied ? 'Mensaje copiado' : 'Copiar mensaje'}
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										<p className="text-xs">
											{messageCopied ? 'Mensaje copiado' : 'Copiar mensaje'}
										</p>
									</TooltipContent>
								</Tooltip>
							</div>

							{/* Compartir */}
							{isMobile ? (
								<button
									onClick={shareNative}
									className="btn-arca-primary w-full flex items-center justify-center gap-2"
								>
									<Share2 className="w-4 h-4" />
									Compartir
								</button>
							) : (
								<div className="space-y-2">
									<p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
										Compartir en
									</p>
									<div className="flex flex-wrap gap-2">
										<Button variant="outline" size="sm" onClick={shareWhatsApp}>
											<SiWhatsapp className="w-4 h-4 mr-2 text-green-600" />
											WhatsApp
										</Button>
										<Button variant="outline" size="sm" onClick={shareTwitter}>
											<SiX className="w-4 h-4 mr-2" />
											Twitter/X
										</Button>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button variant="outline" size="sm" onClick={shareLinkedIn}>
													<SiLinkedin className="w-4 h-4 mr-2 text-blue-600" />
													LinkedIn
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p className="text-xs max-w-[200px]">
													LinkedIn no permite mensaje prefilled. Copia el texto antes si quieres
													añadirlo manualmente.
												</p>
											</TooltipContent>
										</Tooltip>
										<Button variant="outline" size="sm" onClick={shareGmail}>
											<SiGmail className="w-4 h-4 mr-2 text-red-500" />
											Gmail
										</Button>
										<Button variant="outline" size="sm" onClick={shareEmail}>
											<Mail className="w-4 h-4 mr-2" />
											Otro correo
										</Button>
										<Button variant="outline" size="sm" onClick={shareFacebook}>
											<SiFacebook className="w-4 h-4 mr-2 text-blue-600" />
											Facebook
										</Button>
									</div>
								</div>
							)}
						</div>
					</motion.div>

					{/* KPIs — solo cuando hay al menos un invitado */}
					{data.stats.total_invited > 0 && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.2 }}
						>
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
								{[
									{ label: 'Invitados', value: data.stats.total_invited },
									{ label: 'Traspasos', value: data.stats.converted_transfer },
									{ label: 'Premios pendientes', value: data.stats.rewards_pending },
									{ label: 'Premios aplicados', value: data.stats.rewards_applied }
								].map(({ label, value }) => (
									<div key={label} className="surface-soft p-4 text-center">
										<p className="text-2xl font-bold font-heading text-primary">{value}</p>
										<p className="text-xs text-muted-foreground mt-1">{label}</p>
									</div>
								))}
							</div>
						</motion.div>
					)}

					{/* Tabla */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.25 }}
					>
						<div className="surface-card p-5 lg:p-6 space-y-4">
							<h2 className="font-heading font-semibold text-lg">Tus invitados y premios</h2>

							{data.attributions.length === 0 ? (
								<div className="text-center py-12 space-y-3">
									<Gift className="w-6 h-6 mx-auto text-gray-300" />
									<p className="text-muted-foreground max-w-sm mx-auto">
										Aún no has invitado a nadie o ninguno de tus amigos ha iniciado el proceso para
										traspasar su antiguo plan a Arca Digital. En cuanto esto ocurra, verás aquí
										reflejados tus posibles premios.
									</p>
								</div>
							) : (
								<AttributionsTable
									attributions={data.attributions}
									rewards={data.rewards}
									rewardSnapshot={data.reward_snapshot}
								/>
							)}
						</div>
					</motion.div>
				</div>
			</div>
		</TooltipProvider>
	);
};

export default MyReferralsView;
